import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./VisionMissionSection.css";

/**
 * UniTech Engineering — Section 02 « Origine & Équipe » — Premium V2.
 * Le nom/export VisionMissionSection est conservé pour éviter toute
 * modification de Nous_Sommes.jsx et des sections déjà validées.
 *
 * L'image incluse est une illustration générique et NON une photo de
 * l'équipe réelle. Avant publication, remplacer l'image par une
 * photographie authentique autorisée et adapter la légende.
 */
const TEAM_IMAGE = "/images/equipe-ingenierie.png";

const CONTENT = {
  fr: {
    eyebrow: "POURQUOI UNITECH EXISTE",
    title: "La technologie doit simplifier le travail sur le terrain.",
    paragraph1:
      "Les professionnels de l’ascenseur font face à des équipements variés et à de fortes exigences de réactivité.",
    paragraph2:
      "Nous avons créé UniTech Engineering avec une conviction : les bonnes technologies doivent rendre les informations plus accessibles et les interventions plus efficaces.",
    statement: [
      "Moins de complexité. Plus de visibilité.",
      "Des outils vraiment utiles."
    ],
    foundingYear: "2024",
    foundingCaption: "Année de création d’UniTech Engineering",
    teamTitle: "Une équipe tournée vers les réalités du terrain.",
    teamDescription:
      "Ingénierie électronique, systèmes embarqués et solutions connectées : des compétences réunies autour de besoins concrets.",
    imageAlt:
      "Illustration d'une équipe d'ingénierie dans un laboratoire technique ; il ne s'agit pas d'une photographie des collaborateurs d'UniTech Engineering.",
    imageCaption: "Visuel d’illustration"
  },
  en: {
    eyebrow: "WHY UNITECH EXISTS",
    title: "Technology should simplify work in the field.",
    paragraph1:
      "Elevator professionals work with a wide variety of equipment and face growing demands for responsiveness.",
    paragraph2:
      "We founded UniTech Engineering on one belief: good technology should make information easier to access and interventions more effective.",
    statement: [
      "Less complexity. Greater visibility.",
      "Tools that are genuinely useful."
    ],
    foundingYear: "2024",
    foundingCaption: "Year UniTech Engineering was founded",
    teamTitle: "A team focused on real-world needs.",
    teamDescription:
      "Electronics engineering, embedded systems and connected solutions: complementary skills brought together around practical needs.",
    imageAlt:
      "Illustration of an engineering team in a technical laboratory; this is not a photograph of UniTech Engineering employees.",
    imageCaption: "Illustrative image"
  }
};

export default function VisionMissionSection() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = CONTENT[language] || CONTENT.fr;
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("about-origin--in-view");
          observer.disconnect();
        }
      },
      { threshold: 0.14 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about-origin"
      className="about-origin"
      ref={sectionRef}
      aria-labelledby="about-origin-title"
    >
      <div className="about-origin__container">
        <div className="about-origin__grid">
          <div className="about-origin__story">
            <p className="about-origin__eyebrow">{copy.eyebrow}</p>
            <h2 id="about-origin-title" className="about-origin__title">
              {copy.title}
            </h2>
            <p className="about-origin__paragraph">{copy.paragraph1}</p>
            <p className="about-origin__paragraph">{copy.paragraph2}</p>
            <p className="about-origin__statement">
              {copy.statement[0]}
              <span>{copy.statement[1]}</span>
            </p>
          </div>

          <aside className="about-origin__people" aria-labelledby="about-origin-team-title">
            <div
              className="about-origin__year"
              aria-label={`${copy.foundingCaption} : ${copy.foundingYear}`}
            >
              {copy.foundingYear}
            </div>
            <p className="about-origin__year-caption">{copy.foundingCaption}</p>

            <h3 id="about-origin-team-title" className="about-origin__team-heading">
              {copy.teamTitle}
            </h3>

            <figure className="about-origin__team-figure">
              <div className="about-origin__team-image-wrap">
                <img
                  className="about-origin__team-image"
                  src={TEAM_IMAGE}
                  alt={copy.imageAlt}
                  width="1448"
                  height="1086"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </figure>

            <p className="about-origin__team-description">{copy.teamDescription}</p>
          </aside>
        </div>
      </div>
    </section>
  );
}
